
import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Users, ShieldCheck, Crown, MessageCircle, Heart, DollarSign } from "lucide-react";

export default function Admin() {
    const [tab, setTab] = useState("pending");
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);

    const load = async () => {
        const [u, s] = await Promise.all([
            client.get(`/admin/users${tab !== "all" ? `?status=${tab}` : ""}`),
            client.get("/admin/analytics"),
        ]).catch(() => [{ data: [] }, { data: null }]);
        setUsers(u.data || []);
        setStats(s.data);
    };

    useEffect(() => { load(); }, [tab]);

    const act = async (uid, action) => {
        try {
            await client.post(`/admin/users/${uid}/${action}`);
            toast.success(`User ${action}ed`);
            await load();
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">Admin Console</div>
            <h1 className="mt-2 font-serif text-5xl text-navy tracking-tighter">Manage HolyMarry</h1>

            {/* Stats */}
            {stats && (
                <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <StatCard icon={<Users />} label="Total Users" value={stats.total_users} />
                    <StatCard icon={<ShieldCheck />} label="Approved" value={stats.approved} />
                    <StatCard icon={<ShieldCheck />} label="Pending" value={stats.pending} />
                    <StatCard icon={<Crown />} label="Premium" value={stats.premium_users} />
                    <StatCard icon={<MessageCircle />} label="Messages" value={stats.messages_total} />
                    <StatCard icon={<DollarSign />} label="Revenue" value={`$${stats.revenue_total?.toFixed(2)}`} />
                </div>
            )}

            {/* Tabs */}
            <div className="mt-12 flex gap-2 border-b border-navy-100">
                {[["pending","Pending"],["approved","Approved"],["rejected","Rejected"],["all","All"]].map(([k,l]) => (
                    <button key={k} onClick={() => setTab(k)}
                        className={`px-4 py-3 font-sans text-xs uppercase tracking-[0.18em] border-b-2 transition-all ${tab===k?"border-gold text-navy":"border-transparent text-navy-400 hover:text-navy"}`}>
                        {l}
                    </button>
                ))}
            </div>

            {/* Users table */}
            <div className="mt-6 bg-white border border-navy-100 rounded-2xl overflow-hidden">
                <table className="w-full text-sm font-sans">
                    <thead className="bg-linen text-[10px] uppercase tracking-[0.18em] text-navy-500">
                        <tr>
                            <th className="text-left p-4">Name</th>
                            <th className="text-left p-4">Email</th>
                            <th className="text-left p-4">Status</th>
                            <th className="text-left p-4">Role</th>
                            <th className="text-right p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 ? (
                            <tr><td colSpan={5} className="p-8 text-center text-navy-400">No users</td></tr>
                        ) : users.map((u) => (
                            <tr key={u.user_id} className="border-t border-navy-50">
                                <td className="p-4 text-navy font-medium">{u.full_name || u.name}</td>
                                <td className="p-4 text-navy-500">{u.email}</td>
                                <td className="p-4"><Badge status={u.profile_status} /></td>
                                <td className="p-4 text-xs uppercase tracking-wider text-navy-400">{u.role}</td>
                                <td className="p-4 text-right space-x-2">
                                    {u.profile_status !== "approved" && (
                                        <button onClick={() => act(u.user_id, "approve")} data-testid={ADMIN.approve}
                                            className="text-xs uppercase tracking-wider bg-gold text-navy rounded-lg px-3 py-1.5 hover:bg-gold-600 hover:text-white transition-all">
                                            Approve
                                        </button>
                                    )}
                                    {u.profile_status !== "rejected" && u.role !== "admin" && (
                                        <button onClick={() => act(u.user_id, "reject")} data-testid={ADMIN.reject}
                                            className="text-xs uppercase tracking-wider border border-navy-200 text-navy rounded-lg px-3 py-1.5 hover:bg-navy hover:text-white transition-all">
                                            Reject
                                        </button>
                                    )}
                                    {u.role !== "admin" && (
                                        <button onClick={() => act(u.user_id, "ban")} data-testid={ADMIN.ban}
                                            className="text-xs uppercase tracking-wider text-red-600 hover:text-white hover:bg-red-600 border border-red-200 rounded-lg px-3 py-1.5 transition-all">
                                            Ban
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatCard({ icon, label, value }) {
    return (
        <div className="bg-white border border-navy-100 rounded-2xl p-4">
            <div className="h-9 w-9 rounded-full bg-linen text-gold-700 flex items-center justify-center mb-3">{icon}</div>
            <div className="font-serif text-2xl text-navy leading-none">{value ?? 0}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-navy-400 mt-1">{label}</div>
        </div>
    );
}

function Badge({ status }) {
    const map = {
        approved: "bg-gold-100 text-gold-700",
        pending: "bg-linen text-navy-600",
        rejected: "bg-red-50 text-red-600",
    };
    return <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold ${map[status] || "bg-linen text-navy-500"}`}>{status}</span>;
}
